import { Link } from '../../../components/Link';
import {
  Lead,
  LearnCode,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('spf-too-many-dns-lookups');

/**
 * The differentiated asset on this page is the per-provider lookup-cost table. Provider SPF
 * records are public, so the counts are verifiable — but they DO change when a provider edits
 * their own record, which is precisely the failure this page describes. The page says so rather
 * than presenting the numbers as permanent, and gives the reader the dig command to re-count.
 */
export default function SpfLookupLimitPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'SPF permits a maximum of 10 DNS lookups while evaluating a record, counting every include, a, mx, exists and redirect, including nested ones. Cross the limit and evaluation stops with a permanent error, which receivers treat as having no SPF record at all — not as a partial pass.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="Why this breaks setups that nobody touched">
        <p>
          The count is not of the mechanisms in <em>your</em> record. It is of every lookup
          performed while resolving it, including the ones inside your providers’ records. Your
          record can be unchanged for two years and still cross the line the day a provider adds an{' '}
          <Tok>include:</Tok> to theirs.
        </p>
        <p>
          When it happens the result is total, not partial: evaluation halts at the eleventh lookup
          and returns <Tok>permerror</Tok>. Under DMARC a permerror is not a pass, so a domain
          relying on SPF for alignment loses it, everywhere, at once. That is the signature of this
          failure — mail that was fine yesterday failing from every sender simultaneously with no
          change on your side.
        </p>
      </LearnSection>

      <LearnSection title="What counts and what does not">
        <LearnTable
          head={['Mechanism', 'Costs a lookup?', 'Note']}
          rows={[
            ['include:', <Lead key="a">Yes — 1, plus everything inside it</Lead>, 'The compounding one'],
            ['a', 'Yes — 1', 'Plus one more per additional name'],
            ['mx', <Lead key="b">Yes — 1, plus one per MX host returned</Lead>, 'A 5-host MX costs 6, not 1'],
            ['exists:', 'Yes — 1', 'Rare in hand-written records'],
            ['redirect=', 'Yes — 1, plus the target’s own lookups', 'Counts even though it is a modifier'],
            ['ip4: / ip6:', <Lead key="c">No — free</Lead>, 'The basis of every fix below'],
            ['all', 'No', 'Terminates evaluation'],
          ]}
        />
        <p>
          The <Tok>mx</Tok> row is the one that catches people. It is a single token in your record
          and it can cost six lookups on a domain with five mail exchangers. There is a separate,
          less-known cap of 10 on <Tok>ptr</Tok>/<Tok>mx</Tok> host expansions inside a single
          mechanism, but you will hit the main limit first.
        </p>
      </LearnSection>

      <LearnSection title="What each provider costs you">
        <p>
          Counts at the time of writing, resolved from each provider’s published record. Treat them
          as indicative rather than fixed — the number changing under you is the entire subject of
          this page. Re-count yours with the command below rather than trusting any table,
          including this one.
        </p>
        <LearnTable
          head={['Provider', 'Include', 'Approx. lookups']}
          rows={[
            ['Google Workspace', <Tok key="a">_spf.google.com</Tok>, '4'],
            ['Microsoft 365', <Tok key="b">spf.protection.outlook.com</Tok>, '3'],
            ['SendGrid', <Tok key="c">sendgrid.net</Tok>, '3'],
            ['Mailchimp', <Tok key="d">servers.mcsv.net</Tok>, '2'],
            ['Amazon SES (regional)', <Tok key="e">amazonses.com</Tok>, '2'],
            ['Zoho Mail', <Tok key="f">zoho.com</Tok>, '3'],
            ['Brevo', <Tok key="g">spf.brevo.com</Tok>, '2'],
            ['Salesforce', <Tok key="h">_spf.salesforce.com</Tok>, '4'],
            ['HubSpot', <Tok key="i">_spf.hubspot.com</Tok>, '2'],
            ['Zendesk', <Tok key="j">mail.zendesk.com</Tok>, '2'],
          ]}
        />
        <p>
          Add them up and the arithmetic is unforgiving. Google plus Salesforce plus SendGrid is
          already eleven before you have listed a single server of your own — and each{' '}
          <Tok>include:</Tok> token costs one on top of what it resolves to.
        </p>
      </LearnSection>

      <LearnSection title="Counting yours">
        <p>
          The recursive count is what matters, so expanding one level by hand is not enough. Walk
          the tree:
        </p>
        <LearnCode>
          {`dig +short TXT yourdomain.com | grep spf1
# then, for every include: you find, repeat on that name
dig +short TXT _spf.google.com
dig +short TXT sendgrid.net`}
        </LearnCode>
        <p>
          Keep going until nothing new appears, counting one for every{' '}
          <Tok>include</Tok>, <Tok>a</Tok>, <Tok>mx</Tok>, <Tok>exists</Tok> and{' '}
          <Tok>redirect</Tok> encountered anywhere in the tree. Or skip the arithmetic —{' '}
          <Link to="/tools/spf-checker" className="text-accent underline underline-offset-4">
            our SPF checker
          </Link>{' '}
          resolves the chain and reports the count.
        </p>
      </LearnSection>

      <LearnSection title="Getting back under the limit">
        <LearnSubsection title="Remove senders you stopped using">
          <p>
            Always first, always the biggest win, and almost always available. Records accumulate
            providers the way a codebase accumulates dependencies — the trial ESP from three years
            ago is still in there costing three lookups. Nothing else on this list is free; this one
            is.
          </p>
        </LearnSubsection>

        <LearnSubsection title="Replace an include with the IPs behind it">
          <p>
            <Tok>ip4:</Tok> costs nothing. If a provider publishes a stable, small range, inlining
            it converts several lookups into zero:
          </p>
          <LearnCode>
            {`before:  include:mail.example-esp.net        → 2 lookups
after:   ip4:198.51.100.0/24 ip4:203.0.113.9  → 0 lookups`}
          </LearnCode>
          <p>
            The trade is real and worth stating plainly: you have taken on the job of noticing when
            that provider changes their IPs, and they will not tell you. Only do this for providers
            who publish a stable range and document it, and put a calendar reminder against it.
          </p>
        </LearnSubsection>

        <LearnSubsection title="Move bulk sending to a subdomain">
          <p>
            The cleanest structural fix. Each domain gets its own budget of ten, so splitting
            senders across names multiplies the budget instead of rationing it:
          </p>
          <LearnCode>
            {`yourdomain.com        v=spf1 include:_spf.google.com ~all          → 4 of 10
mail.yourdomain.com   v=spf1 include:sendgrid.net ~all            → 3 of 10
news.yourdomain.com   v=spf1 include:servers.mcsv.net ~all        → 2 of 10`}
          </LearnCode>
          <p>
            It also separates reputation: a marketing campaign that generates complaints damages{' '}
            <Tok>news.yourdomain.com</Tok> and leaves your corporate mail alone. That benefit
            usually outlives the lookup problem that prompted the change.
          </p>
        </LearnSubsection>

        <LearnSubsection title="SPF flattening — understand the trade first">
          <p>
            Flattening resolves every include to literal IPs and publishes the result. It always
            works and it is the option most likely to hurt you later:
          </p>
          <LearnList>
            <li>
              <Lead>The record goes stale silently.</Lead> When Google rotates a range, your
              flattened copy still lists the old one. Mail from the new servers fails SPF and
              nothing announces it.
            </li>
            <li>
              <Lead>It gets long.</Lead> Flattening Google alone runs to several hundred bytes,
              which drags in the 255-byte string-splitting rules.
            </li>
            <li>
              <Lead>Automated flatteners add a dependency.</Lead> A third party now controls a DNS
              record that gates your mail, and their outage is your outage.
            </li>
          </LearnList>
          <p>
            Reach for it when subdomain separation is genuinely not available, not as the first
            answer.
          </p>
        </LearnSubsection>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'What is the exact SPF lookup limit?',
    a: 'Ten DNS-querying mechanisms per evaluation, defined in RFC 7208 section 4.6.4. It counts include, a, mx, exists and redirect, including every one encountered inside nested records. Exceeding it must return permerror.',
  },
  {
    q: 'What happens when I exceed it?',
    a: 'Evaluation stops and returns a permanent error. Receivers treat permerror as no SPF result at all, so under DMARC you lose SPF alignment completely. It is not a partial pass and it is not a warning.',
  },
  {
    q: 'Do ip4 and ip6 mechanisms count?',
    a: 'No. They resolve nothing, so they are free. That is why every fix for this problem eventually involves converting lookups into literal addresses, and why a record full of ip4 entries can be long without being at risk.',
  },
  {
    q: 'Why did my SPF suddenly break when I changed nothing?',
    a: 'Because the count includes your providers’ records, not just yours. When a provider adds an include to their own record, every domain that includes them gains that lookup. Domains sitting at nine or ten cross the line the same day, without any change on their side.',
  },
  {
    q: 'Is SPF flattening safe?',
    a: 'It works, but it moves a maintenance burden onto you. Flattened records go stale silently when a provider rotates IP ranges, and mail from the new servers then fails SPF with nothing to announce it. Prefer removing unused senders or splitting across subdomains first.',
  },
  {
    q: 'Does the limit apply per domain or per message?',
    a: 'Per evaluation, which in practice means per domain being checked. Each domain and subdomain gets its own budget of ten — which is exactly why moving bulk senders onto a subdomain is the most durable fix available.',
  },
];
